import { Alert, Badge, Button, Chip, Table } from '@bphxd/ds-core-react';
import { Fragment, ReactElement, useEffect, useMemo } from 'react';
import * as TanstackTable from '@tanstack/react-table';
import {
  Add16,
  Check24,
  Duplicate16,
  DownloadExcel16,
  Edit16,
  Filter16,
  Link16,
  Trash16,
  Preview16,
} from '@bphxd/ds-core-react/lib/icons';
import classes from './AspectsTable.module.scss';
import JobLinkModal from '../JobLink/JobLinkModal';
import { TAspect, TJoint } from '../../../lib/types';
import clsx from 'clsx';
import { defaultSearchFilterFn } from './util';
import DeleteConfirmationModal from '../AspectDeleteConfirmation/DeleteConfirmationModal';
import { useBrowserLocationState } from '../../../lib/util/browser';
import { FormModal, TFormModal } from '../../molecules/FormModal/FormModal';
import ReuseJointModal from './ReuseJointModal';

const ACTION_DISABLED_WHILE_LOADING_MESSAGE = 'Data updation in progress';

const AspectsTableSearch: React.FC<{
  filter: string;
  searchPlaceholderText: string;
  setFilter: (value: string) => void;
}> = ({ filter, setFilter, searchPlaceholderText }) => {
  return (
    <div className={classes.searchWrapper}>
      <Table.Search
        initialValue={filter}
        className="w-100 bg-white"
        onChange={setFilter}
        debounce={300}
        placeholder={searchPlaceholderText}
      />
    </div>
  );
};

export type TAspectsTableFilters<T extends string> = Record<T, string[]>;

type Props<T, S extends string = string> = {
  aspectName: string;
  data: T[];
  onLinkSubmit?: (selection: T[], jobIds: number[]) => Promise<unknown>;
  onRemoveSubmit?: (selection: T[]) => Promise<unknown>;
  onFilterClick?: () => void;
  onDownloadClick?: (selection: T) => void;
  columns: TanstackTable.ColumnDef<T, unknown>[];
  renderAddForm?: (onClose: () => void) => ReactElement;
  renderEditForm?: (onClose: () => void, defaultValue: T) => ReactElement;
  renderDuplicationForm?: (onClose: () => void, defaultValue: T) => ReactElement;
  renderViewForm?: (onClose: () => void) => ReactElement;
  creationDisabled: boolean;
  getRowMutationDisabled: (row: T) => boolean;
  getRowDeletionDisabled: (row: T) => boolean;
  hideSelectionColumn?: boolean;
  renderBare?: boolean;
  title?: ReactElement | string;
  alertText?: string;
  Modal?: TFormModal;
  searchEnabled?: boolean;
  defaultSortingColumn?: string;
  defaultSortingDirection? : boolean;
  hiddenColumn? : string;
  globalFilterFn?: TanstackTable.FilterFnOption<T>;
  filters?: TAspectsTableFilters<S>;
  onUpdateFilters?: (filters: TAspectsTableFilters<S>) => void;
  searchPlaceholderText?: string;
  isLoading?: boolean;
  onFilteredRowsChange?: (rows: TanstackTable.Row<T>[]) => void;
  onJointReuse?: (joint: TJoint) => void;
  certificateType?: string;
  viewAllUrl?: string;
};

function AspectsTable<T extends TAspect>({
  aspectName,
  title,
  data,
  onLinkSubmit,
  onRemoveSubmit,
  onFilterClick,
  onDownloadClick,
  columns,
  renderAddForm,
  renderEditForm,
  renderDuplicationForm,
  renderViewForm,
  hideSelectionColumn,
  renderBare,
  alertText,
  Modal = FormModal,
  searchEnabled = false,
  creationDisabled,
  getRowMutationDisabled,
  getRowDeletionDisabled,
  defaultSortingColumn,
  defaultSortingDirection,
  hiddenColumn,
  globalFilterFn = defaultSearchFilterFn,
  filters,
  onUpdateFilters,
  searchPlaceholderText = '',
  isLoading = false,
  onFilteredRowsChange,
  onJointReuse,
  certificateType,  
  viewAllUrl,
}: Props<T>) {
  const [searchString, setSearchString] = useBrowserLocationState('searchString', '');
  const table = TanstackTable.useReactTable({
    data,
    columns,
    state: {
      globalFilter: useMemo(() => ({ filters, searchString }), [filters, searchString]),
    },
    enableColumnFilters: true,
    enableFilters: true,
    globalFilterFn,
    getCoreRowModel: TanstackTable.getCoreRowModel(),
    getFilteredRowModel: TanstackTable.getFilteredRowModel(),
    getSortedRowModel: TanstackTable.getSortedRowModel(),
    getPaginationRowModel: TanstackTable.getPaginationRowModel(),
    initialState: {
      sorting: defaultSortingColumn ? [{ id: defaultSortingColumn, desc: defaultSortingDirection ?? false }] : [],
      pagination: { pageSize: 25 },
      columnVisibility: hiddenColumn ? {[hiddenColumn]: false} : undefined,
    },
  });

  const aspectBaseName = aspectName || 'aspect';

  useEffect(() => {
    if (onFilteredRowsChange) {
      onFilteredRowsChange(table.getFilteredRowModel().rows);
    }
  }, [table.getFilteredRowModel().rows]);

  const showActions = renderEditForm || onLinkSubmit;
  const filtersCount = Object.values(filters || {}).filter((values) => values.length > 0).length;
  return (
    <div className={`${classes.root} ${renderBare ? classes.bare : ''}`}>
      {renderBare ? (
        title
      ) : (
        <>
          <div className={classes.header}>
            <div className={classes.headerTop}>
              <h2>{title || `${aspectBaseName} Tracker`}</h2>
              <div>
                {/* open view in a modal */}
                {renderViewForm && (
                  <Modal
                    title={`All ${aspectBaseName}s`}
                    size="lg"
                    renderForm={renderViewForm}
                    renderButton={(onClick) => (
                      <Button
                        disabled={isLoading}
                        rounded="0"
                        level="tertiary"
                        Icon={Preview16}
                        onClick={onClick}
                        style={{ marginRight: '4px' }}
                        title={
                          isLoading
                            ? ACTION_DISABLED_WHILE_LOADING_MESSAGE
                            : `View all ${aspectBaseName}s`
                        }
                      >
                        View all {aspectBaseName}s
                      </Button>
                    )}
                  />
                )}
                {/* open view in a new tab */}
                {viewAllUrl && (
                  <Button
                    disabled={isLoading}
                    rounded="0"
                    level="tertiary"
                    Icon={Preview16}
                    onClick={() => window.open(viewAllUrl, '_blank')}
                    style={{ marginRight: '4px' }}
                    title={
                      isLoading
                        ? ACTION_DISABLED_WHILE_LOADING_MESSAGE
                        : `View all ${aspectBaseName}s`
                    }
                  >
                    View all {aspectBaseName}s
                  </Button>
                )}
                {renderAddForm && (
                  <Modal
                    title={`Add new ${aspectBaseName}`}
                    renderForm={renderAddForm}
                    renderButton={(onClick) => (
                      <Button
                        disabled={creationDisabled || isLoading}
                        rounded="0"
                        level="tertiary"
                        Icon={Add16}
                        onClick={onClick}
                        title={
                          isLoading
                            ? ACTION_DISABLED_WHILE_LOADING_MESSAGE
                            : `Add new ${aspectBaseName}`
                        }
                      >
                        Add new {aspectBaseName}
                      </Button>
                    )}
                  />
                )}
                
                {onJointReuse && (
                  <Modal
                    title={`Re-use ${aspectBaseName}`}
                    renderForm={(onClose) => (
                      <ReuseJointModal
                      onJointSelect={(joint) => {
                        if (onJointReuse) {
                          onJointReuse(joint);
                        }
                        onClose();
                      }}
                      onSubmit={(joint) => {
                          if (onJointReuse) {
                            return Promise.resolve(onJointReuse(joint));
                          }
                          return Promise.resolve();
                      }}
                      onClose={onClose}
                      certificateType={certificateType || ''} 
                      isLoading={isLoading}
                    />
                  )}
                    renderButton={(onClick) => (
                      <Button
                        className={classes.reuseJointButton}
                        disabled={creationDisabled || isLoading}
                        rounded="0"
                        level="tertiary"
                        Icon={Duplicate16}
                        onClick={onClick}
                        title={
                          isLoading
                            ? ACTION_DISABLED_WHILE_LOADING_MESSAGE
                            : `Re-use ${aspectBaseName}`
                        }
                      >
                        Re-use {aspectBaseName}
                      </Button>
                    )}
                  />
                )}
              </div>
            </div>
            <div className={classes.notice}>
              {!!alertText && (
                <Alert color="success" Icon={Check24} iconStrokeWidth={2} iconColored>
                  {alertText}
                </Alert>
              )}
            </div>
          </div>

          <div className="d-flex gap-6 mb-5">
            {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
              <div className="d-flex gap-4">
                {renderEditForm && (
                  <Modal
                    title={`Edit ${aspectBaseName}`}
                    renderForm={(onClose) =>
                      renderEditForm(
                        onClose,
                        table.getSelectedRowModel().rows.map((r) => r.original)[0]
                      )
                    }
                    renderButton={(onClick) => (
                      <Button
                        rounded="0"
                        level="tertiary"
                        Icon={Edit16}
                        onClick={onClick}
                        title="Multi edit feature not ready"
                      >
                        Edit
                      </Button>
                    )}
                  />
                )}
                {onLinkSubmit && (
                  <JobLinkModal
                    title={`Link ${aspectBaseName} to job`}
                    selection={table.getSelectedRowModel().rows.map((r) => r.original)}
                    renderButton={(onLinkClick) => (
                      <Button
                        rounded="0"
                        level="tertiary"
                        Icon={Link16}
                        onClick={() => onLinkClick()}
                        title={`Link ${aspectBaseName} to job`}
                      >
                        Link to Job
                      </Button>
                    )}
                    onSubmit={onLinkSubmit}
                  />
                )}
              </div>
            )}
          </div>
        </>
      )}
      {searchEnabled && (
        <div className={classes.searchBar}>
          <div className={classes.filtersSection}>
            {onFilterClick && (
              <>
                <Button title="Filter the jobs" rounded="0" Icon={Filter16} onClick={onFilterClick}>
                  Filter
                  {filtersCount > 0 && (
                    <Badge className={classes.badge} pill color="info">
                      {filtersCount}
                    </Badge>
                  )}
                </Button>
                <div>
                  {Object.entries(filters || {})
                    .filter(([, selection]) => selection.length > 0)
                    .map(([key, selection]) => {
                      return selection.map((value) => {
                        const id = `${key}:${value}`;
                        const label = `${key.replace(/([A-Z])/g, ' $1')}: ${value}`;
                        return (
                          <Chip
                            key={id}
                            color="info"
                            variant="bordered"
                            id={id}
                            label={label}
                            onRemove={(id) => {
                              if (!filters || !onUpdateFilters) return;

                              const [key, valueToBeRemoved] = id.split(':');
                              const newFilters = {
                                ...filters,
                                [key]: filters[key].filter((value) => value !== valueToBeRemoved),
                              };
                              onUpdateFilters(newFilters);
                            }}
                          />
                        );
                      });
                    })}
                </div>
              </>
            )}
          </div>

          <AspectsTableSearch
            filter={searchString}
            setFilter={setSearchString}
            searchPlaceholderText={searchPlaceholderText}
          />
        </div>
      )}
      <Table
        size="sm"
        allSelected={table.getIsAllRowsSelected()}
        onSelectAll={() => table.toggleAllRowsSelected()}
        someSelected={table.getIsSomeRowsSelected()}
        className={(hideSelectionColumn && classes.hideSelectionColumn) || ''}
      >
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.HeaderRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Fragment key={header.id}>
                  {header.isPlaceholder ? (
                    <Table.Header />
                  ) : header.column.getCanSort() ? (
                    <Table.Header
                      onSort={(currentSort) =>
                        header.column.toggleSorting(
                          currentSort === 'desc' ? undefined : currentSort === 'asc'
                        )
                      }
                      sortDirection={header.column.getIsSorted()}
                    >
                      {TanstackTable.flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Table.Header>
                  ) : (
                    <Table.Header>
                      {TanstackTable.flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Table.Header>
                  )}
                </Fragment>
              ))}
              {showActions && <Table.Header key="action">Action</Table.Header>}
            </Table.HeaderRow>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row, index) => {
            const rowData = row.original;
            const isRowMutationDisabled = getRowMutationDisabled(rowData);
            const isRowDeletionDisabled = getRowDeletionDisabled(rowData);

            return (
              <Table.Row
                key={row.id}
                rowIndex={index}
                selected={row.getIsSelected()}
                onSelectChange={(selected) => row.toggleSelected(selected)}
                className={row.getIsSelected() ? classes.active : ''}
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell key={cell.id}>
                    {TanstackTable.flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
                {showActions && (
                  <Table.Cell
                    key="action"
                    title={(isLoading && ACTION_DISABLED_WHILE_LOADING_MESSAGE) || ''}
                  >
                    <div
                      className={clsx({
                        [classes.actions]: true,
                        [classes.disabled]: isLoading,
                      })}
                    >
                      {onDownloadClick && (
                        <DownloadExcel16
                          onClick={() => onDownloadClick(table.getRowModel().rows[index].original)}
                          title={`Download ${aspectBaseName}`}
                        />
                      )}
                      {renderEditForm && (
                        <Modal
                          title={`Edit ${aspectBaseName}`}
                          renderForm={(onClose) => renderEditForm(onClose, row.original)}
                          renderButton={(onClick) => (
                            <Edit16
                              className={clsx({ [classes.disabled]: isRowMutationDisabled })}
                              onClick={isRowMutationDisabled ? undefined : onClick}
                              title={
                                isRowMutationDisabled
                                  ? 'Edit is disabled'
                                  : (isLoading && ACTION_DISABLED_WHILE_LOADING_MESSAGE) ||
                                    `Edit the ${aspectBaseName}`
                              }
                            />
                          )}
                        />
                      )}
                      {renderDuplicationForm && (
                        <Modal
                          title={`Create duplicate ${aspectBaseName}`}
                          renderForm={(onClose) => renderDuplicationForm(onClose, row.original)}
                          renderButton={(onClick) => (
                            <Duplicate16
                              className={clsx({ [classes.disabled]: creationDisabled })}
                              onClick={creationDisabled ? undefined : onClick}
                              title={
                                creationDisabled
                                  ? 'Duplication is disabled'
                                  : (isLoading && ACTION_DISABLED_WHILE_LOADING_MESSAGE) ||
                                    `Duplicate the ${aspectBaseName}`
                              }
                            />
                          )}
                        />
                      )}
                      {onLinkSubmit && (
                        <JobLinkModal
                          title={`Link ${aspectBaseName} to job`}
                          selection={[table.getRowModel().rows[index].original]}
                          renderButton={(onLinkClick) => (
                            <Link16
                              className={clsx({ [classes.disabled]: isRowMutationDisabled })}
                              onClick={isRowMutationDisabled ? undefined : onLinkClick}
                              title={
                                isRowMutationDisabled
                                  ? 'Edit is disabled'
                                  : `Link ${aspectBaseName} to job`
                              }
                            />
                          )}
                          onSubmit={onLinkSubmit}
                        />
                      )}
                      {onRemoveSubmit && (
                        <DeleteConfirmationModal
                          aspectName={aspectBaseName}
                          selection={[table.getRowModel().rows[index].original]}
                          renderButton={(onLinkClick) => (
                            <Trash16
                              className={clsx({ [classes.disabled]: isRowDeletionDisabled })}
                              onClick={isRowDeletionDisabled ? undefined : onLinkClick}
                              title={
                                isRowDeletionDisabled
                                  ? 'Delete is disabled'
                                  : `Remove ${aspectBaseName}`
                              }
                            />
                          )}
                          onSubmit={onRemoveSubmit}
                        />
                      )}
                    </div>
                  </Table.Cell>
                )}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      {!renderBare && (
        <Table.PaginationRow
          totalCount={table.getPrePaginationRowModel().rows.length}
          pageIndex={table.getState().pagination.pageIndex}
          pageSize={table.getState().pagination.pageSize}
          onPageIndexChange={table.setPageIndex}
          onPageSizeChange={table.setPageSize}
        />
      )}
    </div>
  );
}

export default AspectsTable;

AspectsTable.classes = classes;
